import { boxShadows } from '@eversports/design-tokens/box-shadows'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Icon from '@eversports/klimt-primitives/legacy/Icon'
import Processing from '@eversports/klimt-primitives/legacy/Processing'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useEffect, useState } from 'react'

import { PrivacySettingField, PrivacySettingVisibility } from '../../../../App.types'
import EditIcon from '../../../../assets/icons/edit.svg'
import {
  usePrivacySettingButtonMeQuery,
  usePrivacySettingButtonUpdatePrivacySettingsMutation,
} from '../../../../graphql'
import { Localized } from '../../../../localization/react'

import EditPrivacySetting from './EditPrivacySetting'

interface Props {
  field: PrivacySettingField
  onClick?: () => void
}

const PrivacySettingButton = ({ field, onClick }: Props) => {
  const { data } = usePrivacySettingButtonMeQuery()
  const [updatePrivacySetting, { loading }] = usePrivacySettingButtonUpdatePrivacySettingsMutation()
  const [updatedVisibility, setUpdatedVisibility] = useState<PrivacySettingVisibility>()
  const [isEditPrivacySettingOpen, setIsEditPrivacySettingOpen] = useState(false)

  useEffect(() => {
    if (!updatedVisibility) return
    void updatePrivacySetting({ variables: { args: { fieldInputs: [{ field, visibility: updatedVisibility }] } } })
  }, [updatedVisibility])

  const handleVisibilityChange = (visibility: PrivacySettingVisibility) => {
    setUpdatedVisibility(visibility)
    handleClose()
  }

  const handleClick = () => {
    if (onClick) onClick()
    setIsEditPrivacySettingOpen(true)
  }

  const handleClose = () => {
    setIsEditPrivacySettingOpen(false)
  }

  if (!data || !data.me) return null

  const privacySettings = data.me.privacySettings
  const privacySetting = privacySettings.find((setting) => setting.field === field)

  if (!privacySetting) return null

  const visibility = updatedVisibility ?? privacySetting.visibility

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'mediumgray',
          borderRadius: 1,
          boxShadow: boxShadows.default,
          cursor: 'pointer',
          paddingX: 3,
          paddingY: 1,
          flexShrink: 0,
        }}
        onClick={handleClick}
      >
        <Processing isLoading={loading}>
          <Stack direction="row" gap={2}>
            <Text variant="small" color={visibility === PrivacySettingVisibility.PRIVATE ? 'red' : 'carbon'}>
              <Localized id="privacy-setting-button-visibility" params={{ visibility }} />
            </Text>
            <Icon src={EditIcon} color="carbon" size="small" />
          </Stack>
        </Processing>
      </Box>
      <EditPrivacySetting
        selectedVisibility={visibility}
        isOpen={isEditPrivacySettingOpen}
        onClose={handleClose}
        onApply={handleVisibilityChange}
      />
    </>
  )
}

export default PrivacySettingButton
